import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  applyDefaultContractTerms,
  defaultLanguagePack,
  packsFromTemplate,
} from '@/lib/contract-language-packs';
import {
  settingsService,
  type ProposalTemplateConfig,
} from '@/services/settings';
import type { ContractTermsOverrideHandler } from '../types';

export interface DocumentLanguageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing project override, if the current job already has one. */
  projectOverride?: string | null;
  /** Persist the same language onto this project so the estimate updates now. */
  onApplyToProject?: ContractTermsOverrideHandler;
}

/**
 * In-place editor for the company default contract boilerplate.
 * Save writes Settings (future contracts) and this project's override.
 * Cancel / close discards the draft and stays on the estimate.
 */
export function DocumentLanguageDialog({
  open,
  onOpenChange,
  projectOverride,
  onApplyToProject,
}: DocumentLanguageDialogProps) {
  const { toast } = useToast();
  const [template, setTemplate] = useState<ProposalTemplateConfig | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTemplate(null);
      setDraft('');
      setError(null);
      setLoading(false);
      setSaving(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    settingsService
      .getProposalTemplate()
      .then((next) => {
        if (cancelled) return;
        const loaded = next || ({} as ProposalTemplateConfig);
        const orgDefault =
          defaultLanguagePack(packsFromTemplate(loaded))?.contractTermsHtml || '';
        setTemplate(loaded);
        setDraft((projectOverride ?? orgDefault) || '');
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load document language.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectOverride]);

  const close = () => onOpenChange(false);

  const handleSave = async () => {
    if (saving || !template) return;
    setSaving(true);
    setError(null);
    try {
      await settingsService.updateProposalTemplate(
        applyDefaultContractTerms(template, draft),
      );
      if (onApplyToProject) {
        await onApplyToProject(draft);
      }
      toast({
        title: 'Document language saved',
        description: 'Settings and this project now use the updated boilerplate.',
      });
      close();
    } catch {
      setError('Failed to save document language.');
      toast({
        title: 'Error',
        description: 'Failed to save document language.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
        data-testid="document-language-dialog"
      >
        <DialogHeader>
          <DialogTitle>Document Language</DialogTitle>
          <DialogDescription>
            Edit the company default contract boilerplate. Save updates Settings
            for future contracts and applies the same language to this project.
            Cancel returns you to the estimate without changing anything.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading document language…</p>
        ) : (
          <Textarea
            aria-label="Document language"
            data-testid="document-language-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-[240px] font-public-sans text-sm"
          />
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={close}
            data-testid="document-language-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saving || loading || !template}
            onClick={() => {
              void handleSave();
            }}
            data-testid="document-language-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
